import { selectProjectMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";
import { selectComponentsList } from "app/containers/Features/containers/TeamSettings/container/Projects/container/ProjectComponent/redux/selector";
import { selectSprintList } from "app/containers/Features/containers/Sprints/redux/selector";
import {
  selectTagList,
  selectTicketStatus,
  selectTicketTypes,
} from "app/redux/selector";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { priorityOptions } from "../../data";
import {
  environmentData,
  releasesData,
  severityOptionsData,
  targetReleasesData,
} from "../CreateBoardsComponent/data";
import { Filter, FilterOptionItem, FilterPickerType, GroupByTypeEnum } from "../../types";
import { FiltersNew, SEARCHABLE_FILTER_IDS } from "./helper";

const getFilterPickerType = (filterId: GroupByTypeEnum): FilterPickerType => {
  switch (filterId) {
    case GroupByTypeEnum.STATUS:
      return FilterPickerType.STATUS;
    case GroupByTypeEnum.TAGS:
      return FilterPickerType.TAGS;
    case GroupByTypeEnum.DUE_DATE:
    case GroupByTypeEnum.CREATED_DATE:
      return FilterPickerType.DATE_RANGE;
    default:
      return FilterPickerType.CHECKBOX;
  }
};

const getFilterOptionItems = (
  filterId: GroupByTypeEnum,
  sources: {
    ticketTypes: FilterOptionItem[];
    statuses: FilterOptionItem[];
    tags: FilterOptionItem[];
    members: FilterOptionItem[];
    sprints: FilterOptionItem[];
    components: FilterOptionItem[];
  },
): FilterOptionItem[] => {
  switch (filterId) {
    case GroupByTypeEnum.TICKET_TYPE:
      return sources.ticketTypes;
    case GroupByTypeEnum.STATUS:
      return sources.statuses;
    case GroupByTypeEnum.TAGS:
      return sources.tags;
    case GroupByTypeEnum.DUE_DATE:
    case GroupByTypeEnum.CREATED_DATE:
      return [];
    case GroupByTypeEnum.PRIORITY:
      return priorityOptions.map((priority) => ({
        id: priority._id ?? priority.value,
        label: priority.label,
        color: priority.color,
      }));
    case GroupByTypeEnum.RELEASE:
      return releasesData.map((release) => ({
        id: release.id,
        label: release.label,
      }));
    case GroupByTypeEnum.TARGET_RELEASE:
      return targetReleasesData.map((release) => ({
        id: release.id,
        label: release.label,
      }));
    case GroupByTypeEnum.SPRINT:
      return sources.sprints;
    case GroupByTypeEnum.ENVIRONMENT:
      return environmentData.map((environment) => ({
        id: environment.id,
        label: environment.label,
      }));
    case GroupByTypeEnum.SEVERITY:
      return severityOptionsData.map((severity) => ({
        id: severity.id,
        label: severity.id,
        color: severity.color,
      }));
    case GroupByTypeEnum.COMPONENT:
      return sources.components;
    case GroupByTypeEnum.ASSIGNEE:
    case GroupByTypeEnum.CREATOR:
      return sources.members;
    default:
      return [];
  }
};

export const useBoardFilters = (): Filter[] => {
  const ticketTypes = useSelector(selectTicketTypes);
  const ticketStatuses = useSelector(selectTicketStatus);
  const members = useSelector(selectProjectMemberList);
  const sprintList = useSelector(selectSprintList);
  const components = useSelector(selectComponentsList);
  const ticketTags = useSelector(selectTagList);

  const optionSources = useMemo(
    () => ({
      ticketTypes:
        ticketTypes?.map((type) => ({ id: type._id, label: type.name })) ?? [],
      statuses:
        ticketStatuses?.map((status) => ({
          id: status._id,
          label: status.name,
          color: status.color,
        })) ?? [],
      tags:
        ticketTags?.map((tag) => ({
          id: tag._id,
          label: tag.title,
          color: tag.color,
        })) ?? [],
      members:
        members?.map((member) => ({ id: member._id, label: member.name })) ?? [],
      sprints:
        sprintList?.map((sprint) => ({ id: sprint._id, label: sprint.name })) ??
        [],
      components:
        components?.map((component) => ({
          id: component._id,
          label: component.name,
        })) ?? [],
    }),
    [ticketTypes, ticketStatuses, ticketTags, members, sprintList, components],
  );

  return useMemo(
    () =>
      FiltersNew.map((filterMeta) => {
        const filterId = filterMeta.id as GroupByTypeEnum;
        const pickerType = getFilterPickerType(filterId);

        return {
          id: filterMeta.id,
          name: filterMeta.name,
          icon: filterMeta.icon,
          options: getFilterOptionItems(filterId, optionSources),
          isSearchable:
            pickerType !== FilterPickerType.DATE_RANGE &&
            SEARCHABLE_FILTER_IDS.has(filterMeta.id),
          pickerType,
        };
      }),
    [optionSources],
  );
};

export const enrichBoardFilters = (
  filters: Filter[],
  boardFilters: Filter[],
): Filter[] =>
  filters.map((filter) => {
    const dynamicFilter = boardFilters.find(
      (boardFilter) => boardFilter.id === filter.id,
    );
    return dynamicFilter ?? filter;
  });
